import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/** @type {Record<string, Promise<any>>} */
const productJsonCache = {};

/**
 * @param {string} handlePath
 */
function loadProductJson(handlePath) {
  if (!productJsonCache[handlePath]) {
    productJsonCache[handlePath] = fetch(`${handlePath}.js`).then((response) => response.json());
  }
  return productJsonCache[handlePath];
}

/**
 * @param {number} cents
 */
function formatMoney(cents) {
  const currency = /** @type {any} */ (window).Shopify?.currency?.active || 'USD';
  const locale = document.documentElement.lang || 'en';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('change', this.onPurchaseOptionChange);
    window.addEventListener('load', this.onPurchaseOptionChange, { once: true });
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('change', this.onPurchaseOptionChange);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    this.onPurchaseOptionChange();
  };

  onPurchaseOptionChange = () => {
    requestAnimationFrame(() => this.applySellingPlanPrice());
  };

  async applySellingPlanPrice() {
    const container = this.querySelector('[ref="priceContainer"]');
    if (!(container instanceof HTMLElement) || container.hasAttribute('data-bundle-product')) return;

    const section = this.closest('.shopify-section, dialog');
    const form = section?.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const sellingPlanField = form.querySelector('[name="selling_plan"]');
    if (!(sellingPlanField instanceof HTMLInputElement || sellingPlanField instanceof HTMLSelectElement)) return;

    const variantField = form.querySelector('[name="id"]');
    if (!(variantField instanceof HTMLInputElement || variantField instanceof HTMLSelectElement)) return;

    const handleMatch = window.location.pathname.match(/\/products\/[^/?#]+/);
    if (!handleMatch) return;

    const priceEl = container.querySelector('[data-bundle-price-value], .price');
    if (!priceEl) return;

    try {
      const product = await loadProductJson(handleMatch[0]);
      const variant = product.variants.find((v) => String(v.id) === String(variantField.value));
      if (!variant) return;

      let cents = variant.price;
      const planId = sellingPlanField.value;

      if (planId) {
        const allocation = variant.selling_plan_allocations?.find(
          (a) => String(a.selling_plan_id) === String(planId)
        );
        if (allocation) cents = allocation.price;
      }

      const formatted = formatMoney(cents);
      if (priceEl.textContent?.trim() !== formatted) {
        priceEl.textContent = formatted;
      }
    } catch (error) {
      console.error('product-price: could not update selling plan price', error);
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}